import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Autocomplete,
  Checkbox,
  Paper,
  CircularProgress,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { ArrowBackIosOutlined, Close, ExpandMore } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import PrintModal from "../components/billing/modal/print-modal";
import BillModal from "../components/billing/modal/bill-modal";
import { EditIcon } from "../comps/CustomIcons";
import CancelDischargeModal from "../components/billing/modal/cancel-discharge-modal";
import PaymentModal from "../components/billing/modal/payment-modal";
import GenerateBillModal from "../components/billing/modal/generate-bill-modal";
import { useParams, useNavigate } from "react-router-dom";
import {
  cancelExpensesByIdApi,
  getBillingByIdApi,
  printPaymentPrintApi,
} from "../services/billing.service";
import { toast } from "react-toastify";
import GroupedItemsAccordion from "../components/billing/GroupedItemsAccordion";
import RefundModal from "../components/billing/modal/refund-modal";
import RefundExpenseModal from "../components/billing/modal/refund-expense-modal";
import RefundPaymentModal from "../components/billing/modal/refund-payment-modal";
import dayjs from "dayjs";

const Billing = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [cancelDischargeModalOpen, setCancelDischargeModalOpen] =
    useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundExpenseModalOpen, setRefundExpenseModalOpen] = useState(false);
  const [refundPaymentModalOpen, setRefundPaymentModalOpen] = useState(false);
  const [reliefModalOpen, setReliefModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentPrintLoading, setPaymentPrintLoading] = useState([]);

  // Replace useGetApi with direct state management
  const [billingData, setBillingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Add state for selected room charge rows
  const [selectedRoomRows, setSelectedRoomRows] = useState([]);

  // Fetch billing data
  const fetchBillingData = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await getBillingByIdApi({ id });
      if (response?.data) {
        setBillingData(response.data);
      } else {
        setIsError(true);
        setBillingData(null);
      }
    } catch (error) {
      setIsError(true);
      setBillingData(null);
      toast.error("Failed to fetch billing information");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchBillingData();
    }
  }, [id]);

  // Extract data from the response
  const allData = billingData?.invoices || [];
  const patientInfo = billingData?.patientInfo || null;
  const hasBillingData = allData && allData.length > 0;

  const refetch = fetchBillingData;

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };
  const handleBoxClick = (index) => {
    setSelectedIndex(index);
  };

  // Handle individual row selection
  const handleRowSelect = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Handle "Select All" checkbox
  const handleSelectAll = () => {
    const expenses = selectedBilling?.invoiceDetails?.expenses ?? [];
    const nonCancelledRows = expenses
      .filter((row) => row.status !== "Cancelled")
      .map((row) => row._id);

    setSelectedRows((prevSelected) => {
      if (nonCancelledRows.length === 0) {
        toast.error("All the expenses are cancelled");
        return [];
      }
      return prevSelected.length === nonCancelledRows.length
        ? []
        : nonCancelledRows;
    });
  };
  const cancelExpensesHandler = async () => {
    let response;
    if (selectedRows.length === 0) {
      toast.error("No expenses selected for cancellation.");
      return;
    }

    const payload = {
      invoiceItemIds: selectedRows.map((id) => id),
    };
    setLoading(true);
    try {
      response = await cancelExpensesByIdApi(
        selectedBilling?.invoiceId,
        payload
      );
      if (response.code === 200 || response.code === 201) {
        toast.success(`Expenses cancelled successfully`);
        refetch();
        setSelectedRows([]);
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(response?.message || "Some error occurred.");
      }
    } catch (error) {
      setLoading(false);
      toast.error(error);
    }
  };

  const selectedBilling = allData?.filter((row) =>
    row?.invoiceNumber?.toString().toLowerCase().includes(search?.toLowerCase())
  )?.[selectedIndex];

  const invoiceExpenses = Array.isArray(selectedBilling?.invoiceDetails?.expenses)
    ? selectedBilling.invoiceDetails.expenses
    : [];
  const nonCancelledExpenses = invoiceExpenses.filter(
    (row) => row.status !== "Cancelled"
  );
  const roomChargeExpenses = invoiceExpenses.filter(
    (row) => row.category === "Room"
  );

  const handleBillModalOpen = () => {
    if (!selectedBilling?.invoiceId) {
      toast.error("Cannot reopen bill: Missing invoice ID");
      return;
    }
    setBillModalOpen(true);
  };
  const handleBillModalClose = () => {
    setBillModalOpen(false);
  };
  const handleGenerateBillModalOpen = () => {
    setGenerateBillModalOpen(true);
  };
  const handleGenerateBillModalClose = () => {
    setGenerateBillModalOpen(false);
  };
  const handlePrintModalOpen = () => {
    setPrintModalOpen(true);
  };
  const handlePrintModalClose = () => {
    setPrintModalOpen(false);
  };
  const handleCancelDischargeModalOpen = () => {
    setCancelDischargeModalOpen(true);
  };
  const handleCancelDischargeModalClose = () => {
    setCancelDischargeModalOpen(false);
  };
  const handlePaymentModalOpen = () => {
    setPaymentModalOpen(true);
  };
  const handlePaymentModalClose = () => {
    setPaymentModalOpen(false);
  };
  const handleRefundModalOpen = () => {
    setRefundModalOpen(true);
  };
  const handleRefundModalClose = () => {
    setRefundModalOpen(false);
  };
  
  const handleRefundExpenseModalOpen = (expense) => {
    setSelectedExpense(expense);
    setRefundExpenseModalOpen(true);
  };
  
  const handleRefundExpenseModalClose = () => {
    setRefundExpenseModalOpen(false);
    setSelectedExpense(null);
  };
  
  const handleRefundPaymentModalOpen = (payment) => {
    setSelectedPayment(payment);
    setRefundPaymentModalOpen(true);
  };
  
  const handleRefundPaymentModalClose = () => {
    setRefundPaymentModalOpen(false);
    setSelectedPayment(null);
  };
  
  const handleReliefModalOpen = () => {
    setReliefModalOpen(true);
  };
  const handleReliefModalClose = () => {
    setReliefModalOpen(false);
  };

  const handlePaymentPrint = async (_id, index) => {
    // Set loading to true only for the clicked index
    setPaymentPrintLoading((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });

    const response = await printPaymentPrintApi(_id);

    // Set loading to false after the API call
    setPaymentPrintLoading((prev) => {
      const updated = [...prev];
      updated[index] = false;
      return updated;
    });

    // Check for error from api.request
    if (!response || response.error) {
      toast.error("Failed to fetch PDF");
      return;
    }

    // Check if response is valid ArrayBuffer or Blob-compatible
    if (
      !(
        response instanceof ArrayBuffer ||
        response instanceof Blob ||
        response.byteLength
      )
    ) {
      toast.error("Invalid PDF data received");
      return;
    }

    const blob = new Blob([response], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const printWindow = window.open(blobUrl, "_blank");

    // Handle popup blocker
    if (!printWindow) {
      console.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    // Optional: Delay to give time for the PDF to load before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Add Select All logic for Room Charges
  const handleSelectAllRoomCharges = () => {
    const expenses = selectedBilling?.invoiceDetails?.expenses ?? [];
    const allRoomRows = expenses
      .filter((row) => row.category === "Room")
      .map((row) => row._id || row.description);
    setSelectedRoomRows((prev) =>
      prev.length === allRoomRows.length ? [] : allRoomRows
    );
  };

  // Add row selection logic
  const handleRoomRowSelect = (id) => {
    setSelectedRoomRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", py: "20px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => navigate(-1)}>
              <ArrowBackIosOutlined sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Back</Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              {(selectedBilling?.status === "Closed" || 
                selectedBilling?.status === "Closed_IPD" ||
                selectedBilling?.status === "Cancelled" ||
                selectedBilling?.status === "Paid") && 
                selectedBilling?.invoiceId && (
                  <Button
                    variant="outlined"
                    sx={{ 
                      minWidth: "150px", 
                      textTransform: "none",
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      "&:hover": {
                        borderColor: "#1565c0",
                        backgroundColor: "rgba(25, 118, 210, 0.04)"
                      }
                    }}
                    onClick={handleBillModalOpen}
                    title={`Click to reopen this bill. Current status: ${selectedBilling?.status}. You will need to provide a reason for reopening.`}
                  >
                    Reopen Bill
                  </Button>
                )}

              {(selectedBilling?.status === "Open" ||
                selectedBilling?.category === "IPD") && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: "150px", textTransform: "none" }}
                  onClick={handleRefundModalOpen}
                >
                  Refund
                </Button>
              )}
              {(selectedBilling?.status === "Open" ||
                selectedBilling?.category === "IPD") && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: "150px", textTransform: "none" }}
                  onClick={handlePaymentModalOpen}
                >
                  Payment
                </Button>
              )}
              {/* {selectedBilling?.status === "Closed_IPD" && (
              <Button
                variant="outlined"
                sx={{ minWidth: "150px", textTransform: "none" }}
                onClick={handleCancelDischargeModalOpen}
              >
                Cancel Discharge
              </Button>
             )}  */}
              {(selectedBilling?.status === "Open" ||
                selectedBilling?.category === "IPD") && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: "150px", textTransform: "none" }}
                  onClick={handleGenerateBillModalOpen}
                >
                  Generate Bill
                </Button>
              )}
              <Button
                variant="outlined"
                sx={{ minWidth: "150px", textTransform: "none" }}
                onClick={handlePrintModalOpen}
              >
                Print
              </Button>

              {(selectedBilling?.status === "Open" ||
                selectedBilling?.category === "IPD") && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: "150px", textTransform: "none" }}
                  onClick={handleReliefModalOpen}
                >
                  Relief
                </Button>
              )}

              {/* Modals */}
              <BillModal
                open={billModalOpen}
                onClose={handleBillModalClose}
                id={selectedBilling?.invoiceId}
                refetch={refetch}
                billStatus={selectedBilling?.status}
              />
              <GenerateBillModal
                open={generateBillModalOpen}
                onClose={handleGenerateBillModalClose}
                id={selectedBilling?.invoiceId}
              />
              <PrintModal
                open={printModalOpen}
                onClose={handlePrintModalClose}
                id={selectedBilling?.invoiceId}
              />
              <CancelDischargeModal
                open={cancelDischargeModalOpen}
                onClose={handleCancelDischargeModalClose}
                id={selectedBilling?.invoiceId}
              />
              <PaymentModal
                open={paymentModalOpen}
                onClose={handlePaymentModalClose}
                id={selectedBilling?.invoiceId}
                refetch={refetch}
              />
              <RefundModal
                open={refundModalOpen}
                onClose={handleRefundModalClose}
                id={selectedBilling?.invoiceId}
                refetch={refetch}
              />
              
              {/* Refund Expense Modal */}
              <RefundExpenseModal
                open={refundExpenseModalOpen}
                onClose={handleRefundExpenseModalClose}
                expense={selectedExpense}
                invoiceId={selectedBilling?.invoiceId}
                refetch={refetch}
              />
              
              {/* Refund Payment Modal */}
              <RefundPaymentModal
                open={refundPaymentModalOpen}
                onClose={handleRefundPaymentModalClose}
                payment={selectedPayment}
                invoiceId={selectedBilling?.invoiceId}
                refetch={refetch}
              />
              
              {/* Relief Modal - Navigate to AddRelief page */}
              {reliefModalOpen && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      maxWidth: 500,
                      width: '90%',
                      maxHeight: '80vh',
                      overflow: 'auto'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Apply Relief</Typography>
                      <Button onClick={handleReliefModalClose} sx={{ minWidth: 'auto' }}>
                        <Close />
                      </Button>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Relief functionality will be available here. You can navigate to the Relief page to manage relief settings.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={handleReliefModalClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleReliefModalClose();
                          navigate('/add-relief', { 
                            state: { 
                              patientId: id,
                              patientData: patientInfo,
                              fromBilling: true 
                            } 
                          });
                        }}
                      >
                        Go to Relief Page
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
          {/* Patient Information */}
          <Box
            sx={{
              border: `1px solid #CCD4DA`,
              borderRadius: "12px",
              p: "20px",
              mb: "30px",
            }}
          >
            <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
              Patient Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="UHID"
                  fullWidth
                  value={patientInfo?.UHID || "-"}
                  required
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Name"
                  value={patientInfo?.name || "-"}
                  fullWidth
                  required
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Number"
                  fullWidth
                  value={patientInfo?.contactNumber || "-"}
                  required
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Gender"
                  value={patientInfo?.gender || "-"}
                  fullWidth
                  required
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              {(selectedBilling?.status === "Closed_IPD" ||
                selectedBilling?.status === "Open_IPD") && (
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label="Bed Category"
                    fullWidth
                    required
                    slotProps={{ input: { readOnly: true } }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Billing ID"
                  fullWidth
                  value={patientInfo?.patientId || "-"}
                  required
                  slotProps={{ input: { readOnly: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  label="Billing Date"
                  value={
                    patientInfo?.registrationDate
                      ? dayjs(patientInfo?.registrationDate).format(
                          "DD/MM/YYYY"
                        )
                      : "-"
                  }
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Loading State */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading billing information...</Typography>
            </Box>
          )}

          {/* Error State */}
          {isError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <Typography color="error">Failed to load billing information. Please try again.</Typography>
            </Box>
          )}

          {/* Empty State - No Billing Data */}
          {!isLoading && !isError && !hasBillingData && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '200px',
              border: '1px solid #CCD4DA',
              borderRadius: '12px',
              p: 4
            }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#5D6A82' }}>
                No Billing Records Found
              </Typography>
              <Typography sx={{ color: '#5D6A82', textAlign: 'center' }}>
                This patient has no billing records or invoices in the system.
              </Typography>
              <Typography sx={{ color: '#5D6A82', textAlign: 'center', mt: 1 }}>
                Bills will appear here once consultations or services are added for this patient.
              </Typography>
            </Box>
          )}

          {/* Main Billing Content - Only show when data exists */}
          {!isLoading && !isError && hasBillingData && (
          <Box sx={{ display: "flex", gap: 3 }}>
            {/* Left Side Section */}
            <Box
              sx={{
                minWidth: "250px",
                maxWidth: "300px",
                height: "calc(100vh - 450px)",
                overflowY: "auto",
                border: "1px solid #92A6CC",
                borderRadius: "12px",
                p: "5px",
                boxSizing: "border-box",
              }}
            >
              <TextField
                label="Search..."
                value={search || ""}
                onChange={handleSearch}
                fullWidth
                size="small"
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  bgcolor: "white",
                  my: 0.5,
                }}
              />
              <List component="nav">
                {allData
                  ?.filter((row) =>
                    row?.invoiceNumber
                      ?.toString()
                      .toLowerCase()
                      .includes(search?.toLowerCase())
                  )
                  ?.map((row, index) => {
                    return (
                      <ListItem
                        button
                        key={row?.invoiceId || row?._id}
                        sx={{
                          backgroundColor:
                            selectedIndex === index ? "#DFE8EF" : "transparent",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          borderRadius: "8px",
                          mb: "5px",
                          overflow: "hidden",
                        }}
                        onClick={() => handleBoxClick(index)}
                      >
                        <ListItemText primary={row.invoiceNumber} />
                        <ListItemText
                          primary={dayjs(row?.invoiceDate).format("DD/MM/YYYY")}
                        />
                      </ListItem>
                    );
                  })}
              </List>
            </Box>

            {/* Main Bill Details Section */}
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ mb: "30px" }}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      Bill Details : {selectedBilling?.category || "-"}
                    </Typography>

                    <Chip
                      label={selectedBilling?.status}
                      color={
                        selectedBilling?.status === "Closed"
                          ? "error"
                          : "success"
                      }
                    />
                  </Box>
                </Grid>
                {/* Amount to be paid */}
                <Grid item xs={12} sm={6} lg={5}>
                  <Box
                    sx={{
                      border: "1px solid #92A6CC",
                      borderRadius: "12px",
                      p: 1.5,
                      overflow: "hidden",
                      height: "180px",
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ fontWeight: 600 }}
                        >
                          Amount To Be Paid:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="error">
                          ₹{selectedBilling?.amountToBePaid || "0"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#5D6A82" }}
                        >
                          Approriate
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          No
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#5D6A82" }}
                        >
                          Bill Of Supply No.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#5D6A82", wordWrap: "break-word" }}
                        >
                          {selectedBilling?.invoiceNumber || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#5D6A82" }}
                        >
                          Generated On
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          {dayjs(selectedBilling?.invoiceDate).format("DD/MM/YYYY")}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#5D6A82" }}
                        >
                          Bill Type
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          -
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* total expense */}
                <Grid item xs={12} sm={6} lg={4}>
                  <Box
                    sx={{
                      border: "1px solid #92A6CC",
                      borderRadius: "12px",
                      p: 1.5,
                      height: "180px",
                      overflow: "hidden",
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Total Expense:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2">
                          ₹{selectedBilling?.totalExpense || ""}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Total Scheme
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹0.00
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Total Insurance
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹0.00
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Total Pharm Return
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹0.00
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Waive Amount
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹0.00
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Total Refunds
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹{selectedBilling?.billingInfo?.totalRefunded || "0.00"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">Net Expense</Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2">-</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                {/* self */}
                <Grid item xs={12} sm={6} lg={3}>
                  <Box
                    sx={{
                      border: "1px solid #92A6CC",
                      borderRadius: "12px",
                      p: 1.5,
                      height: "180px",
                      overflow: "hidden",
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Self
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Payment
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹{selectedBilling?.billingInfo?.totalPayments || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Used
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹{selectedBilling?.billingInfo?.totalUsed || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Available
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹
                          {selectedBilling?.billingInfo?.availableBalance ||
                            "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Refunded
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹ {selectedBilling?.billingInfo?.totalRefunded || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          Round Off
                        </Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" sx={{ color: "#5D6A82" }}>
                          ₹{selectedBilling?.billingInfo?.roundedOff || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {/* Expenses Accordion */}
              <Accordion expanded={expanded} onChange={handleAccordionChange}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ backgroundColor: "#f5f5f5" }}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: "15px",
                    }}
                  >
                    <Typography component="span">Expenses</Typography>
                                      </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#f5f5f5" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={
                          nonCancelledExpenses.length > 0 &&
                          selectedRows.length === nonCancelledExpenses.length
                        }
                        onChange={handleSelectAll}
                      />
                      <Typography>Select All</Typography>
                    </Box>
                    <Button
                      sx={{ textTransform: "none", height: "30px" }}
                      variant="outlined"
                      onClick={cancelExpensesHandler}
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress size={"20px"} />
                      ) : (
                        "Cancel Charges"
                      )}
                    </Button>
                  </Box>
                  <Table>
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { textAlign: "center" },
                      }}
                    >
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Item Name</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoiceExpenses.map((row) => (
                        <TableRow key={row?._id}>
                          <TableCell padding="checkbox">
                            {row.status !== "Cancelled" && (
                              <Checkbox
                                checked={selectedRows.includes(row?._id)}
                                onChange={() => handleRowSelect(row?._id)}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                row.status === "Cancelled" ? "red" : "inherit",
                            }}
                          >
                            {row?.item?.itemName || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                row.status === "Cancelled" ? "red" : "inherit",
                            }}
                          >
                            {row?.quantity || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                row.status === "Cancelled" ? "red" : "inherit",
                            }}
                          >
                            ₹{row?.unitPrice || "-"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                row.status === "Cancelled" ? "red" : "inherit",
                            }}
                          >
                            ₹{row?.amount || "-"}
                          </TableCell>
                          <TableCell>
                            {row.status !== "Cancelled" && (
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ 
                                  textTransform: "none",
                                  borderColor: "#ff9800",
                                  color: "#ff9800",
                                  "&:hover": {
                                    borderColor: "#f57c00",
                                    backgroundColor: "rgba(255, 152, 0, 0.04)"
                                  }
                                }}
                                onClick={() => handleRefundExpenseModalOpen(row)}
                                title="Refund this expense"
                              >
                                Refund
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>

              {/* Room Charges Accordion */}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ backgroundColor: "#f5f5f5" }}
                  aria-controls="panel-room-charges-content"
                  id="panel-room-charges-header"
                >
                  <Typography component="span">Room Charges</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#f5f5f5" }}>
                  <Table>
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { textAlign: "center" },
                      }}
                    >
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              roomChargeExpenses.length > 0 &&
                              selectedRoomRows.length ===
                                roomChargeExpenses.length
                            }
                            onChange={handleSelectAllRoomCharges}
                          />
                        </TableCell>
                        <TableCell>Room Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Start Date/Time</TableCell>
                        <TableCell>End Date/Time</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Net Amount</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roomChargeExpenses.map((row) => (
                          <TableRow key={row._id || row.description}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedRoomRows.includes(row._id || row.description)}
                                onChange={() => handleRoomRowSelect(row._id || row.description)}
                              />
                            </TableCell>
                            <TableCell>{row?.itemName || row?.item?.itemName || "-"}</TableCell>
                            <TableCell>{row?.itemCode || row?.item?.itemCode || "-"}</TableCell>
                            <TableCell>{row?.startDate ? dayjs(row.startDate).format("DD/MM/YYYY HH:mm") : "-"}</TableCell>
                            <TableCell>{row?.endDate ? dayjs(row.endDate).format("DD/MM/YYYY HH:mm") : "-"}</TableCell>
                            <TableCell>{row?.quantity || "-"}</TableCell>
                            <TableCell>₹{row?.unitPrice || "-"}</TableCell>
                            <TableCell>₹{row?.amount || "-"}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ 
                                  textTransform: "none",
                                  borderColor: "#ff9800",
                                  color: "#ff9800",
                                  "&:hover": {
                                    borderColor: "#f57c00",
                                    backgroundColor: "rgba(255, 152, 0, 0.04)"
                                  }
                                }}
                                onClick={() => handleRefundExpenseModalOpen(row)}
                                title="Refund this room charge"
                              >
                                Refund
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ backgroundColor: "#f5f5f5" }}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: "15px" }}>
                    <Typography component="span">Payments</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#f5f5f5" }}>
                  <Table>
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { textAlign: "center" },
                      }}
                    >
                      <TableRow>
                        <TableCell>Transaction Type</TableCell>
                        <TableCell>Payment Date</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Amount Paid</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBilling?.invoiceDetails?.payments?.map(
                        (row, index) => (
                          <TableRow key={row?._id}>
                            <TableCell>
                              {row?.transactionType?.name || "-"}
                            </TableCell>
                            <TableCell>
                              {dayjs(row?.paymentDate).format("DD/MM/YYYY")}
                            </TableCell>
                            <TableCell>{row?.remarks || "-"}</TableCell>
                            <TableCell>₹{row?.amountPaid || "-"}</TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ textTransform: "none" }}
                                  onClick={() =>
                                    handlePaymentPrint(row?._id, index)
                                  }
                                  disabled={paymentPrintLoading[index]}
                                >
                                  {paymentPrintLoading[index] ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    `Print`
                                  )}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    textTransform: "none",
                                    borderColor: "#ff9800",
                                    color: "#ff9800",
                                    "&:hover": {
                                      borderColor: "#f57c00",
                                      backgroundColor: "rgba(255, 152, 0, 0.04)"
                                    }
                                  }}
                                  onClick={() => handleRefundPaymentModalOpen(row)}
                                  title="Refund this payment"
                                >
                                  Refund
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
              <GroupedItemsAccordion
                groupedItems={selectedBilling?.invoiceDetails?.groupedItems}
              />
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ backgroundColor: "#f5f5f5" }}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: "15px" }}>
                    <Typography component="span">Refunds</Typography>
                    <Typography component="span" sx={{ color: "#5D6A82", fontSize: "14px" }}>
                      Total: ₹{selectedBilling?.billingInfo?.totalRefunded || "0.00"}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#f5f5f5" }}>
                  {selectedBilling?.invoiceDetails?.refunds && 
                   selectedBilling?.invoiceDetails?.refunds.length > 0 ? (
                    <Table>
                      <TableHead
                        sx={{
                          backgroundColor: "#f5f5f5",
                          "& th": { textAlign: "left" },
                        }}
                      >
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBilling?.invoiceDetails?.refunds?.map(
                          (row, index) => (
                            <TableRow key={row?._id}>
                              <TableCell>
                                {dayjs(row?.date).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell>₹{row?.amount || "-"}</TableCell>
                              <TableCell>{row?.reason || "-"}</TableCell>
                              <TableCell>
                                {row?.invoiceItem ? "Expense Refund" : "Payment Refund"}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      py: 3,
                      color: '#5D6A82'
                    }}>
                      <Typography>No refunds have been processed yet.</Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls="panel3-content"
                  id="panel3-header"
                  sx={{ backgroundColor: "#f5f5f5" }}
                >
                  <Typography component="span">Waive Amount</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "#f5f5f5" }}>
                  <Table>
                    <TableHead
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": { textAlign: "center" },
                      }}
                    >
                      {console.log(
                        selectedBilling?.invoiceDetails,
                        "res array"
                      )}

                      <TableRow>
                        <TableCell>
                          {
                            selectedBilling?.invoiceDetails?.waiversAndDiscounts
                              ?.normalDiscount?.discountAmount
                          }
                        </TableCell>
                        <TableCell>
                          {
                            selectedBilling?.invoiceDetails?.waiversAndDiscounts
                              ?.normalDiscount?.discountPercentage
                          }
                        </TableCell>
                        <TableCell>
                          {selectedBilling?.invoiceDetails?.waiversAndDiscounts
                            ?.normalDiscount?.discountReason || "N/A"}
                        </TableCell>
                        <TableCell>
                          {
                            selectedBilling?.invoiceDetails?.waiversAndDiscounts
                              ?.normalDiscount?.discountType
                          }
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Billing;
